import React, { useState, useEffect } from 'react';
import {
    FaUser, FaEnvelope, FaPhone, FaBuilding, FaMapMarkerAlt,
    FaCheckCircle, FaArrowRight, FaArrowLeft, FaCreditCard,
    FaShieldAlt, FaCalendarAlt, FaClock, FaUsers,
    FaInfoCircle, FaStar, FaGraduationCap, FaFileAlt,
    FaWhatsapp, FaChartLine, FaLaptopCode, FaUserTie,
    FaPrint, FaBookmark, FaBook, FaDesktop, FaHome, FaSpinner
} from 'react-icons/fa';
import axios from 'axios';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { useLocation } from 'react-router-dom';
import { API_URL as API_BASE, resolveImageUrl } from '../config/api';

// Helper pour extraire le nombre d'un prix (ex: "250€" → 250, "1 900 €" → 1900)
const parsePrice = (priceString) => {
    if (typeof priceString === 'number') return priceString;
    if (!priceString) return 0;
    // Extraire tous les chiffres et les joindre
    const numbers = priceString.toString().match(/\d+/g);
    return numbers ? parseInt(numbers.join('')) : 0;
};

const fmtPrice = (price) => new Intl.NumberFormat('fr-FR').format(parsePrice(price)) + ' €';
const fmtDate  = (d)     => new Date(d).toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' });

const RegisterFormation = () => {
    const location = useLocation();
    // Récupérer les données de session depuis le state (si venant de DowSession)
    const sessionFromState = location.state?.session;
    // Récupérer les données de certification depuis le state (si venant de CertificationsCINEF)
    const certificationFromState = location.state?.certification;
    
    // Déterminer le type et l'ID
    const formationType = certificationFromState ? 'certification' : 'session';
    const formationId = certificationFromState?.id || sessionFromState?.id || new URLSearchParams(window.location.search).get('session') || 1;

    //  État session (peut être une session ou une certification)
    const [session,        setSession]        = useState(sessionFromState || certificationFromState || null);
    const [sessionLoading, setSessionLoading] = useState(!sessionFromState && !certificationFromState); // Pas de loading si données déjà disponibles
    const [sessionError,   setSessionError]   = useState(null);

    //  État formulaire 
    const [currentStep,       setCurrentStep]       = useState(1);
    const [showSessionDetails,setShowSessionDetails] = useState(false);
    const [isSubmitting,      setIsSubmitting]       = useState(false);
    const [isSuccess,         setIsSuccess]          = useState(false);
    const [apiError,          setApiError]           = useState(null);
    const [confirmData,       setConfirmData]        = useState(null); // données retournées par l'API

    const [formData, setFormData] = useState({
        typeFormation:   '',
        civilite:        '',
        nom:             '',
        prenom:          '',
        email:           '',
        telephone:       '',
        entreprise:      '',
        poste:           '',
        secteur:         '',
        themeFormation:  '',
        nombrePersonnes: '1',
        adresse:         '',
        ville:           '',
        pays:            'Bénin',
        hebergement:     false,
        transport:       false,
        restauration:    true,
        modePaiement:    '',
        motivation:      '',
        accepteConditions: false,
        newsletter:      true,
    });

    const [calculatedPrice, setCalculatedPrice] = useState(0);

    //  Charger la session depuis l'API uniquement si pas déjà disponible
    useEffect(() => {
        // Si on a déjà les données depuis le state (session ou certification), pas besoin de charger
        if (sessionFromState || certificationFromState) {
            setCalculatedPrice(parsePrice(sessionFromState?.price || certificationFromState?.price) || 0);
            return;
        }

        const loadSession = async () => {
            try {
                setSessionLoading(true);
                // Utiliser l'endpoint approprié selon le type pour le chargement
                const endpoint = formationType === 'certification' 
                    ? `${API_BASE}/formations/certifications/${formationId}`
                    : `${API_BASE}/formations/sessions/${formationId}`;
                const res = await axios.get(endpoint);
                setSession(res.data);
                setCalculatedPrice(parsePrice(res.data.price));
            } catch (e) {
                console.error('Erreur chargement formation:', e);
                setSessionError("Impossible de charger les informations de la formation.");
            } finally {
                setSessionLoading(false);
            }
        };
        loadSession();
    }, [formationId, formationType, sessionFromState, certificationFromState]);

    //  Recalculer le prix 
    useEffect(() => {
        if (!session) return;
        const nb = parseInt(formData.nombrePersonnes) || 1;
        const basePrice = parsePrice(session.price);
        let total = basePrice * nb;
        if (formData.typeFormation === 'presentiel') {
            if (formData.hebergement)  total += 75 * nb;
            if (formData.transport)    total += 40 * nb;
            // Restauration offerte (0€)
        }
        setCalculatedPrice(total);
    }, [formData.hebergement, formData.transport, formData.restauration,
        formData.nombrePersonnes, formData.typeFormation, session]);

    //  Handlers 
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const isStepValid = (step) => {
        switch (step) {
            case 1: return formData.typeFormation && formData.civilite && formData.nom &&
                           formData.prenom && formData.email && formData.telephone &&
                           formData.themeFormation && formData.nombrePersonnes;
            case 2: return formData.entreprise && formData.poste && formData.secteur;
            case 3: return formData.motivation;
            case 4: return formData.modePaiement && formData.accepteConditions;
            default: return true;
        }
    };

    const nextStep = () => {
        if (isStepValid(currentStep) && currentStep < 4) {
            setCurrentStep(s => s + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };
    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(s => s - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    //  Soumission 
    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError(null);
        setIsSubmitting(true);

        try {
            const payload = {
                typeFormation:    formData.typeFormation,
                civilite:         formData.civilite,
                nom:              formData.nom,
                prenom:           formData.prenom,
                email:            formData.email,
                telephone:        formData.telephone,
                entreprise:       formData.entreprise,
                poste:            formData.poste,
                secteur:          formData.secteur,
                themeFormation:   formData.themeFormation,
                nombrePersonnes:  parseInt(formData.nombrePersonnes),
                motivation:       formData.motivation,
                modePaiement:     formData.modePaiement,
                accepteConditions: formData.accepteConditions ? '1' : '0',
                hebergement:      formData.hebergement,
                transport:        formData.transport,
                restauration:     formData.restauration,
                ville:            formData.ville,
                pays:             formData.pays,
                adresse:          formData.adresse,
                newsletter:       formData.newsletter,
            };

            // Utiliser la route sessions/register pour tout (sessions et certifications)
            const res = await axios.post(`${API_BASE}/formations/sessions/${formationId}/register`, payload);

            setConfirmData({
                confirmationNumber: res.data.confirmation_number,
                totalPrice:         res.data.total_price,
            });
            setIsSuccess(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });

        } catch (err) {
            if (err.response?.status === 422) {
                // Erreurs de validation Laravel
                const errors = err.response.data.errors;
                const firstError = errors
                    ? Object.values(errors).flat()[0]
                    : err.response.data.message;
                setApiError(firstError);
            } else {
                setApiError(err.response?.data?.message || "Une erreur est survenue. Veuillez réessayer.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    //  Chargement / Erreur session 
    if (sessionLoading) {
        return (
            <>
                <Header />
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
                    minHeight:'60vh', gap:16, fontSize:18, color:'#6b7280', fontFamily:'Inter,sans-serif' }}>
                    <FaSpinner style={{ animation:'spin 1s linear infinite', fontSize:28 }} />
                    Chargement de la session…
                    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                </div>
                <Footer />
            </>
        );
    }

    if (sessionError || !session) {
        return (
            <>
                <Header />
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                    minHeight:'60vh', gap:16, fontFamily:'Inter,sans-serif', padding:24 }}>
                    <div style={{ fontSize:48 }}>😕</div>
                    <h2 style={{ color:'#1a202c' }}>Session introuvable</h2>
                    <p style={{ color:'#6b7280' }}>{sessionError}</p>
                    <button onClick={() => window.history.back()}
                        style={{ padding:'12px 24px', background:'#198754', color:'#fff',
                            border:'none', borderRadius:10, fontWeight:700, cursor:'pointer' }}>
                        Retour
                    </button>
                </div>
                <Footer />
            </>
        );
    }

    //  Page succès 
    if (isSuccess && confirmData) {
        return (
            <>
                <div className="inscription-wrapper">
                    <div className="success-page">
                        <div className="success-container">
                            <div className="success-animation">
                                <div className="success-checkmark"><FaCheckCircle /></div>
                            </div>

                            <h1 className="success-title">🎉 Inscription Confirmée !</h1>
                            <p className="success-subtitle">
                                Félicitations {formData.prenom} ! Votre inscription a été enregistrée avec succès.
                            </p>

                            <div className="confirmation-card-modern">
                                <div className="confirmation-badge">
                                    <FaCheckCircle className="badge-icon" /><span>CONFIRMÉ</span>
                                </div>

                                <div className="confirmation-header-modern">
                                    <div className="confirmation-icon-wrapper">
                                        <FaFileAlt className="confirmation-icon" />
                                    </div>
                                    <div>
                                        <h3>Numéro de confirmation</h3>
                                        <p className="confirmation-number">{confirmData.confirmationNumber}</p>
                                    </div>
                                </div>

                                <div className="confirmation-details-modern">
                                    <div className="detail-card">
                                        <div className="detail-icon"><FaGraduationCap /></div>
                                        <div className="detail-content">
                                            <span className="detail-label">Formation</span>
                                            <span className="detail-value">{session.title}</span>
                                        </div>
                                    </div>
                                    <div className="detail-card">
                                        <div className="detail-icon"><FaBook /></div>
                                        <div className="detail-content">
                                            <span className="detail-label">Thème souhaité</span>
                                            <span className="detail-value">{formData.themeFormation}</span>
                                        </div>
                                    </div>
                                    <div className="detail-card">
                                        <div className="detail-icon">
                                            {formData.typeFormation === 'presentiel' ? <FaHome /> : <FaDesktop />}
                                        </div>
                                        <div className="detail-content">
                                            <span className="detail-label">Type de formation</span>
                                            <span className="detail-value">
                                                {formData.typeFormation === 'presentiel' ? 'Présentiel' : 'En ligne'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="detail-card">
                                        <div className="detail-icon"><FaUsers /></div>
                                        <div className="detail-content">
                                            <span className="detail-label">Participants</span>
                                            <span className="detail-value">
                                                {formData.nombrePersonnes} {parseInt(formData.nombrePersonnes) > 1 ? 'personnes' : 'personne'}
                                            </span>
                                        </div>
                                    </div>
                                    {session.startDate && (
                                        <div className="detail-card">
                                            <div className="detail-icon"><FaCalendarAlt /></div>
                                            <div className="detail-content">
                                                <span className="detail-label">Date de début</span>
                                                <span className="detail-value">{fmtDate(session.startDate)}</span>
                                            </div>
                                        </div>
                                    )}
                                    {session.city && (
                                        <div className="detail-card">
                                            <div className="detail-icon"><FaMapMarkerAlt /></div>
                                            <div className="detail-content">
                                                <span className="detail-label">Lieu</span>
                                                <span className="detail-value">{session.city}</span>
                                            </div>
                                        </div>
                                    )}
                                    <div className="detail-card highlight">
                                        <div className="detail-icon"><FaCreditCard /></div>
                                        <div className="detail-content">
                                            <span className="detail-label">Montant total</span>
                                            <span className="detail-value price-highlight">
                                                {fmtPrice(calculatedPrice)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Détail du calcul */}
                                {parseInt(formData.nombrePersonnes) > 1 && (
                                    <div style={{ 
                                        background: '#f0fdf4', 
                                        padding: '20px', 
                                        borderRadius: '12px', 
                                        marginTop: '20px',
                                        border: '2px solid #dcfce7'
                                    }}>
                                        <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#198754', marginBottom: '12px' }}>
                                            Détail du calcul
                                        </h4>
                                        <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.8' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                <span>Prix unitaire :</span>
                                                <span style={{ fontWeight: '600' }}>{fmtPrice(session.price)}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                <span>Nombre de participants :</span>
                                                <span style={{ fontWeight: '600' }}>× {formData.nombrePersonnes}</span>
                                            </div>
                                            {formData.typeFormation === 'presentiel' && (
                                                <>
                                                    {formData.hebergement && (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#198754' }}>
                                                            <span>+ Hébergement :</span>
                                                            <span style={{ fontWeight: '600' }}>{fmtPrice(75 * parseInt(formData.nombrePersonnes))}</span>
                                                        </div>
                                                    )}
                                                    {formData.transport && (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#198754' }}>
                                                            <span>+ Transport :</span>
                                                            <span style={{ fontWeight: '600' }}>{fmtPrice(40 * parseInt(formData.nombrePersonnes))}</span>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            <div style={{ 
                                                borderTop: '2px solid #198754', 
                                                marginTop: '12px', 
                                                paddingTop: '12px',
                                                display: 'flex', 
                                                justifyContent: 'space-between',
                                                fontSize: '16px',
                                                fontWeight: '700',
                                                color: '#198754'
                                            }}>
                                                <span>Total calculé :</span>
                                                <span>{fmtPrice(calculatedPrice)}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="participant-info">
                                    <h4>Informations du participant principal</h4>
                                    <div className="participant-details">
                                        <div className="participant-item"><FaUser /><span>{formData.civilite} {formData.nom} {formData.prenom}</span></div>
                                        <div className="participant-item"><FaEnvelope /><span>{formData.email}</span></div>
                                        <div className="participant-item"><FaPhone /><span>{formData.telephone}</span></div>
                                        <div className="participant-item"><FaBuilding /><span>{formData.entreprise} — {formData.poste}</span></div>
                                    </div>
                                </div>
                            </div>

                            <div className="next-steps-modern">
                                <h3>📋 Prochaines Étapes</h3>
                                <div className="steps-grid">
                                    <div className="step-card">
                                        <div className="step-number-modern">1</div>
                                        <div className="step-icon-modern"><FaEnvelope /></div>
                                        <h4>Vérifiez votre email</h4>
                                        <p>Un email de confirmation a été envoyé à <strong>{formData.email}</strong></p>
                                    </div>
                                    <div className="step-card">
                                        <div className="step-number-modern">2</div>
                                        <div className="step-icon-modern"><FaCreditCard /></div>
                                        <h4>Effectuez le paiement</h4>
                                        <p>Suivez les instructions de paiement reçues par email</p>
                                    </div>
                                    <div className="step-card">
                                        <div className="step-number-modern">3</div>
                                        <div className="step-icon-modern"><FaFileAlt /></div>
                                        <h4>Recevez vos documents</h4>
                                        <p>Convocation et supports de formation sous 48h</p>
                                    </div>
                                </div>
                            </div>

                            <div className="success-actions-modern">
                                <button className="btn-action primary" onClick={() => window.print()}>
                                    <FaPrint /> Imprimer la confirmation
                                </button>
                                <button className="btn-action outline" onClick={() => window.location.href = '/'}>
                                    Retour aux sessions
                                </button>
                            </div>

                            <div className="contact-support-modern">
                                <div className="support-icon"><FaWhatsapp /></div>
                                <div className="support-content">
                                    <h4>Besoin d'aide ?</h4>
                                    <p>Notre équipe est disponible pour vous accompagner</p>
                                    <a href="https://wa.me/2290163186060" className="whatsapp-link">
                                        <FaWhatsapp /> Contacter sur WhatsApp : +229 01 63 18 60 60
                                    </a>
                                </div>
                            </div>

                            <div className="catalogue-link">
                                <FaBook />
                                <p>Consultez notre catalogue :
                                    <a href="https://cinef-bj.com/formations/docs/catalogue_cinef_2026.pdf" target="_blank" rel="noopener noreferrer">
                                        Catalogue CINEF 2026
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                    <style>{styles}</style>
                </div>
            </>
        );
    }

    //  Formulaire principal 
    return (
        <>
            <Header />
            <div className="inscription-wrapper">
                {/* Header session */}
                <div className="inscription-header">
                    <div className="inscription-container">
                        <button className="btn-back" onClick={() => window.history.back()}>
                            <FaArrowLeft /> Retour
                        </button>
                        <div className="header-session-card" onClick={() => setShowSessionDetails(!showSessionDetails)}>
                            <img src={resolveImageUrl(session.image)} alt={session.title} className="session-thumb" />
                            <div className="session-info-header">
                                <h2>{session.title}</h2>
                                <div className="session-meta">
                                    {session.startDate && <span><FaCalendarAlt /> {fmtDate(session.startDate)}</span>}
                                    {session.city && <span><FaMapMarkerAlt /> {session.city}</span>}
                                    {session.seatsAvailable && <span><FaUsers /> {session.seatsAvailable} places</span>}
                                    {session.duration && !session.startDate && <span><FaClock /> {session.duration}</span>}
                                    {session.domain && <span><FaBook /> {session.domain}</span>}
                                </div>
                            </div>
                            <div className="header-price">
                                <div className="price-label">À partir de</div>
                                <div className="price-amount">{fmtPrice(session.price)}</div>
                            </div>
                            <button className="btn-details">
                                <FaInfoCircle /> {showSessionDetails ? 'Masquer' : 'Détails'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Session détails */}
                {showSessionDetails && (
                    <div className="session-details-expanded">
                        <div className="inscription-container">
                            <div className="details-grid">
                                <div className="detail-section">
                                    <h3><FaBookmark /> Description</h3>
                                    <p>{session.description}</p>
                                </div>
                                <div className="detail-section">
                                    <h3><FaUserTie /> Niveau</h3>
                                    <div className="instructor-card">
                                        <div className="instructor-avatar">{session.level?.[0] || 'N'}</div>
                                        <div>
                                            <p className="instructor-name">{session.level}</p>
                                            <div className="instructor-rating">
                                                <FaStar /> {session.domain}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Formulaire */}
                <section className="inscription-main">
                    <div className="inscription-container">
                        <div className="inscription-layout">
                            <div className="form-section">
                                <div className="progress-bar">
                                    <div className="progress-fill" style={{ width:`${(currentStep/4)*100}%` }}></div>
                                </div>
                                <div className="progress-steps">
                                    {[1,2,3,4].map(step => (
                                        <div key={step} className={`step ${currentStep >= step ? 'active' : ''}`}>
                                            <div className="step-circle">
                                                {currentStep > step ? <FaCheckCircle /> : step}
                                            </div>
                                            <div className="step-label">
                                                {step===1&&'Informations'}{step===2&&'Professionnel'}
                                                {step===3&&'Motivation'}{step===4&&'Paiement'}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Erreur API */}
                                {apiError && (
                                    <div style={{ background:'#fef2f2', border:'1px solid #fecaca',
                                        borderRadius:10, padding:'14px 18px', marginBottom:20,
                                        color:'#dc2626', fontSize:14, fontWeight:600,
                                        display:'flex', alignItems:'center', gap:10 }}>
                                        ⚠️ {apiError}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="inscription-form">
                                    {/*  ÉTAPE 1  */}
                                    {currentStep === 1 && (
                                        <div className="form-step">
                                            <h2 className="step-title">Informations Personnelles</h2>
                                            <p className="step-description">Commencez par renseigner vos informations de base</p>

                                            <div className="type-formation-card">
                                                <div className="card-header">
                                                    <FaLaptopCode className="card-icon" />
                                                    <h3>Type de Formation</h3>
                                                </div>
                                                <div className="card-body">
                                                    <div className="form-group">
                                                        <label>Choisissez le mode de formation *</label>
                                                        <div className="type-formation-options">
                                                            {[
                                                                {v:'presentiel', icon:<FaHome className="type-icon"/>, title:'Présentiel', desc:`Formation en salle à ${session.city||'Cotonou'}`},
                                                                {v:'ligne',      icon:<FaDesktop className="type-icon"/>, title:'En ligne', desc:'Formation à distance via internet'},
                                                            ].map(opt => (
                                                                <label key={opt.v} className={`type-option ${formData.typeFormation===opt.v?'selected':''}`}>
                                                                    <input type="radio" name="typeFormation" value={opt.v}
                                                                        checked={formData.typeFormation===opt.v} onChange={handleChange} required/>
                                                                    {opt.icon}
                                                                    <div className="type-info">
                                                                        <span className="type-title">{opt.title}</span>
                                                                        <span className="type-desc">{opt.desc}</span>
                                                                    </div>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="theme-formation-card">
                                                <div className="card-header">
                                                    <FaBook className="card-icon" />
                                                    <h3>Thème de Formation</h3>
                                                </div>
                                                <div className="card-body">
                                                    <div className="form-group">
                                                        <label>Thème souhaité *</label>
                                                        <input type="text" name="themeFormation" value={formData.themeFormation}
                                                            onChange={handleChange} placeholder="Ex: Gestion de projet, Management…" required/>
                                                        <p className="help-text">
                                                            Consultez notre <a href="https://cinef-bj.com/formations/docs/catalogue_cinef_2026.pdf" target="_blank" rel="noopener noreferrer">catalogue CINEF 2026</a>
                                                        </p>
                                                    </div>
                                                    <div className="form-group">
                                                        <label><FaUsers /> Nombre de personnes *</label>
                                                        <select name="nombrePersonnes" value={formData.nombrePersonnes} onChange={handleChange} required>
                                                            {[...Array(20)].map((_,i) => (
                                                                <option key={i+1} value={i+1}>{i+1} {i===0?'personne':'personnes'}</option>
                                                            ))}
                                                        </select>
                                                        {parseInt(formData.nombrePersonnes) > 1 && (
                                                            <p className="help-text">Tarif de groupe : {fmtPrice(calculatedPrice)}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="form-group">
                                                <label>Civilité *</label>
                                                <div className="radio-group">
                                                    {['M.','Mme','Mlle'].map(c => (
                                                        <label key={c} className="radio-label">
                                                            <input type="radio" name="civilite" value={c}
                                                                checked={formData.civilite===c} onChange={handleChange}/>
                                                            <span>{c}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label><FaUser /> Nom *</label>
                                                    <input type="text" name="nom" value={formData.nom}
                                                        onChange={handleChange} placeholder="Votre nom" required/>
                                                </div>
                                                <div className="form-group">
                                                    <label><FaUser /> Prénom *</label>
                                                    <input type="text" name="prenom" value={formData.prenom}
                                                        onChange={handleChange} placeholder="Votre prénom" required/>
                                                </div>
                                            </div>

                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label><FaEnvelope /> Email *</label>
                                                    <input type="email" name="email" value={formData.email}
                                                        onChange={handleChange} placeholder="votre.email@exemple.com" required/>
                                                </div>
                                                <div className="form-group">
                                                    <label><FaPhone /> Téléphone *</label>
                                                    <input type="tel" name="telephone" value={formData.telephone}
                                                        onChange={handleChange} placeholder="+229 XX XX XX XX" required/>
                                                </div>
                                            </div>

                                            {formData.typeFormation === 'presentiel' && (
                                                <>
                                                    <div className="form-row">
                                                        <div className="form-group">
                                                            <label><FaMapMarkerAlt /> Ville *</label>
                                                            <input type="text" name="ville" value={formData.ville}
                                                                onChange={handleChange} placeholder="Votre ville" required/>
                                                        </div>
                                                        <div className="form-group">
                                                            <label><FaMapMarkerAlt /> Pays</label>
                                                            <input type="text" name="pays" value={formData.pays}
                                                                onChange={handleChange} placeholder="Votre pays"/>
                                                        </div>
                                                    </div>
                                                    <div className="form-group">
                                                        <label><FaMapMarkerAlt /> Adresse complète</label>
                                                        <input type="text" name="adresse" value={formData.adresse}
                                                            onChange={handleChange} placeholder="Votre adresse"/>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {/*  ÉTAPE 2  */}
                                    {currentStep === 2 && (
                                        <div className="form-step">
                                            <h2 className="step-title">Informations Professionnelles</h2>
                                            <p className="step-description">Parlez-nous de votre parcours professionnel</p>

                                            <div className="form-group">
                                                <label><FaBuilding /> Entreprise / Organisation *</label>
                                                <input type="text" name="entreprise" value={formData.entreprise}
                                                    onChange={handleChange} placeholder="Nom de votre entreprise" required/>
                                            </div>
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label><FaUserTie /> Poste Actuel *</label>
                                                    <input type="text" name="poste" value={formData.poste}
                                                        onChange={handleChange} placeholder="Votre fonction" required/>
                                                </div>
                                                <div className="form-group">
                                                    <label><FaChartLine /> Secteur d'Activité *</label>
                                                    <select name="secteur" value={formData.secteur} onChange={handleChange} required>
                                                        <option value="">Choisissez un secteur</option>
                                                        <option value="public">Secteur Public</option>
                                                        <option value="prive">Secteur Privé</option>
                                                        <option value="associatif">Associatif / ONG</option>
                                                        <option value="independant">Indépendant / Consultant</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/*  ÉTAPE 3  */}
                                    {currentStep === 3 && (
                                        <div className="form-step">
                                            <h2 className="step-title">Motivations</h2>
                                            <p className="step-description">Aidez-nous à mieux comprendre vos objectifs</p>
                                            <div className="form-group">
                                                <label>Pourquoi souhaitez-vous suivre cette formation ? *</label>
                                                <textarea name="motivation" value={formData.motivation}
                                                    onChange={handleChange} placeholder="Décrivez vos motivations…"
                                                    rows="5" required></textarea>
                                                <div className="char-count">{formData.motivation.length} / 500</div>
                                            </div>
                                        </div>
                                    )}

                                    {/*  ÉTAPE 4  */}
                                    {currentStep === 4 && (
                                        <div className="form-step">
                                            <h2 className="step-title">Modalités de Paiement</h2>
                                            <p className="step-description">Choisissez votre mode de paiement</p>

                                            <div className="form-group">
                                                <label>Mode de Paiement *</label>
                                                <div className="payment-methods">
                                                    {[
                                                        {v:'carte',    icon:<FaCreditCard/>, label:'Carte Bancaire'},
                                                        {v:'mobile',   icon:<FaPhone/>,      label:'Mobile Money'},
                                                        {v:'virement', icon:<FaBuilding/>,   label:'Virement Bancaire'},
                                                        {v:'especes',  icon:<FaCreditCard/>, label:'Espèces'},
                                                    ].map(p => (
                                                        <label key={p.v} className={`payment-card ${formData.modePaiement===p.v?'selected':''}`}>
                                                            <input type="radio" name="modePaiement" value={p.v}
                                                                checked={formData.modePaiement===p.v} onChange={handleChange}/>
                                                            <span className="payment-icon">{p.icon}</span>
                                                            <span>{p.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="payment-info-box">
                                                <FaInfoCircle />
                                                <p>Paiement unique de <strong>{fmtPrice(calculatedPrice)}</strong></p>
                                            </div>

                                            <div className="conditions-section">
                                                <label className="checkbox-label">
                                                    <input type="checkbox" name="accepteConditions"
                                                        checked={formData.accepteConditions} onChange={handleChange} required/>
                                                    <span>J'accepte les <a href="/conditions-vente" target="_blank" rel="noopener noreferrer">conditions générales</a> et la
                                                        <a href="/confidentialite" target="_blank" rel="noopener noreferrer"> politique de confidentialité</a> *</span>
                                                </label>
                                                <label className="checkbox-label">
                                                    <input type="checkbox" name="newsletter"
                                                        checked={formData.newsletter} onChange={handleChange}/>
                                                    <span>Je souhaite recevoir les actualités CINEF par email</span>
                                                </label>
                                            </div>

                                            <div className="security-badge">
                                                <FaShieldAlt className="security-icon" />
                                                <p><strong>Paiement 100% Sécurisé</strong><br/>Vos données sont protégées et cryptées</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Navigation */}
                                    <div className="form-navigation">
                                        {currentStep > 1 && (
                                            <button type="button" onClick={prevStep} className="btn-prev">
                                                <FaArrowLeft /> Précédent
                                            </button>
                                        )}
                                        <div className="nav-spacer"></div>
                                        {currentStep < 4 ? (
                                            <button type="button" onClick={nextStep} className="btn-next"
                                                disabled={!isStepValid(currentStep)}>
                                                Suivant <FaArrowRight />
                                            </button>
                                        ) : (
                                            <button type="submit" className="btn-submit"
                                                disabled={isSubmitting || !isStepValid(currentStep)}>
                                                {isSubmitting
                                                    ? <><FaSpinner style={{animation:'spin 1s linear infinite'}}/> Envoi en cours…</>
                                                    : <><FaCheckCircle /> Confirmer l'inscription</>
                                                }
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>

                            {/* Sidebar récapitulatif */}
                            <div className="sidebar-recap">
                                <div className="recap-card sticky">
                                    <h3>Récapitulatif</h3>
                                    <div className="recap-session">
                                        <img src={resolveImageUrl(session.image)} alt={session.title} />
                                        <div className="recap-session-info">
                                            <h4>{session.title}</h4>
                                            <div className="recap-meta">
                                                {session.startDate && <span><FaCalendarAlt /> {fmtDate(session.startDate)}</span>}
                                                {session.duration && <span><FaClock /> {session.duration}</span>}
                                                {session.city && <span><FaMapMarkerAlt /> {session.city}</span>}
                                                {session.domain && !session.city && <span><FaBook /> {session.domain}</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="recap-divider"></div>
                                    <div className="recap-details">
                                        {formData.typeFormation && (
                                            <div className="recap-line special">
                                                <span>{formData.typeFormation==='presentiel'?<FaHome/>:<FaDesktop/>} {formData.typeFormation==='presentiel'?'Présentiel':'En ligne'}</span>
                                            </div>
                                        )}
                                        <div className="recap-line">
                                            <span>Formation</span>
                                            <span className="price">{fmtPrice(session.price)}</span>
                                        </div>
                                        {parseInt(formData.nombrePersonnes) > 1 && (
                                            <div className="recap-line special">
                                                <span><FaUsers /> {formData.nombrePersonnes} personnes</span>
                                                <span className="price">{fmtPrice(parsePrice(session.price) * parseInt(formData.nombrePersonnes))}</span>
                                            </div>
                                        )}
                                        {formData.typeFormation==='presentiel' && formData.hebergement && (
                                            <div className="recap-line option"><span><FaBuilding /> Hébergement</span><span className="price">+{fmtPrice(75*parseInt(formData.nombrePersonnes))}</span></div>
                                        )}
                                        {formData.typeFormation==='presentiel' && formData.transport && (
                                            <div className="recap-line option"><span><FaMapMarkerAlt /> Transport</span><span className="price">+{fmtPrice(40*parseInt(formData.nombrePersonnes))}</span></div>
                                        )}
                                        {formData.typeFormation==='presentiel' && formData.restauration && (
                                            <div className="recap-line" style={{ color: '#198754' }}><span><FaClock /> Restauration</span><span className="price" style={{ color: '#198754', fontWeight: '600' }}>Offert</span></div>
                                        )}
                                    </div>
                                    <div className="recap-divider"></div>
                                    <div className="recap-total">
                                        <span>Total</span>
                                        <span className="total-price">{fmtPrice(calculatedPrice)}</span>
                                    </div>
                                    <div className="recap-payment-info"><FaInfoCircle /><span>Paiement unique</span></div>
                                    <div className="recap-features">
                                        {['Certificat inclus','Support de cours','Accès plateforme'].map(f => (
                                            <div key={f} className="feature-item"><FaCheckCircle /><span>{f}</span></div>
                                        ))}
                                    </div>
                                    <div className="help-box">
                                        <FaWhatsapp className="help-icon"/>
                                        <div><strong>Besoin d'aide ?</strong><p>+229 63 18 60 60</p></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <style>{styles}</style>
            </div>
            <Footer />
        </>
    );
};

export default RegisterFormation;

//  CSS (identique à l'original + spin animation) 
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  @keyframes spin { to { transform: rotate(360deg); } }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  .inscription-wrapper { font-family: 'Inter', sans-serif; background: #fafafa; min-height: 100vh; }
  .inscription-container { max-width: 1280px; margin: 0 auto; padding: 0 24px; margin-top: 60px; }
  .inscription-header { background: white; border-bottom: 1px solid #e5e7eb; padding: 20px 0; position: sticky; top: 0; z-index: 100; box-shadow: 0 2px 8px rgba(0,0,0,0.05); }
  .inscription-header .inscription-container { display: flex; align-items: center; gap: 24px; }
  .btn-back { display: flex; align-items: center; gap: 8px; padding: 10px 20px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 10px; font-size: 14px; font-weight: 600; color: #374151; cursor: pointer; transition: all 0.3s ease; font-family: inherit; }
  .btn-back:hover { background: #f3f4f6; border-color: #d1d5db; }
  .header-session-card { flex: 1; display: flex; align-items: center; gap: 16px; padding: 12px; background: #f9fafb; border-radius: 12px; cursor: pointer; transition: all 0.3s ease; }
  .header-session-card:hover { background: #f3f4f6; }
  .session-thumb { width: 60px; height: 60px; border-radius: 10px; object-fit: cover; }
  .session-info-header { flex: 1; }
  .session-info-header h2 { font-size: 16px; font-weight: 700; color: #1a202c; margin-bottom: 4px; }
  .session-meta { display: flex; gap: 16px; font-size: 13px; color: #6b7280; }
  .session-meta span { display: flex; align-items: center; gap: 4px; }
  .header-price { text-align: right; }
  .price-label { font-size: 11px; color: #9ca3af; margin-bottom: 2px; }
  .price-amount { font-size: 18px; font-weight: 900; color: #198754; }
  .btn-details { display: flex; align-items: center; gap: 6px; padding: 8px 16px; background: white; border: 1px solid #e5e7eb; border-radius: 8px; font-size: 13px; font-weight: 600; color: #374151; cursor: pointer; transition: all 0.3s ease; font-family: inherit; }
  .btn-details:hover { border-color: #198754; color: #198754; }
  .session-details-expanded { background: white; border-bottom: 1px solid #e5e7eb; padding: 32px 0; }
  .details-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 32px; }
  .detail-section h3 { display: flex; align-items: center; gap: 10px; font-size: 18px; font-weight: 700; color: #1a202c; margin-bottom: 16px; }
  .detail-section h3 svg { color: #198754; }
  .detail-section p { font-size: 15px; color: #6b7280; line-height: 1.7; }
  .instructor-card { display: flex; align-items: center; gap: 16px; padding: 16px; background: #f9fafb; border-radius: 12px; }
  .instructor-avatar { width: 60px; height: 60px; border-radius: 50%; background: linear-gradient(135deg,#198754,#20c997); display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; font-weight: 700; }
  .instructor-name { font-size: 16px; font-weight: 700; color: #1a202c; margin-bottom: 4px; }
  .instructor-rating { display: flex; align-items: center; gap: 6px; font-size: 14px; color: #fbbf24; font-weight: 600; }
  .inscription-main { padding: 40px 0 80px; }
  .inscription-layout { display: grid; grid-template-columns: 1.5fr 1fr; gap: 40px; align-items: start; }
  .form-section { background: white; border-radius: 24px; padding: 40px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
  .type-formation-card { background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 2px solid #3b82f6; border-radius: 16px; padding: 24px; margin-bottom: 32px; }
  .type-formation-card .card-header { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
  .type-formation-card .card-icon { font-size: 28px; color: #3b82f6; }
  .type-formation-card h3 { font-size: 20px; font-weight: 700; color: #1a202c; }
  .type-formation-options { display: grid; grid-template-columns: repeat(2,1fr); gap: 16px; }
  .type-option { display: flex; align-items: center; gap: 16px; padding: 20px; background: white; border: 2px solid #e5e7eb; border-radius: 12px; cursor: pointer; transition: all 0.3s ease; position: relative; }
  .type-option input { position: absolute; opacity: 0; }
  .type-option:hover { border-color: #198754; }
  .type-option.selected { background: #f0fdf4; border-color: #198754; }
  .type-icon { font-size: 32px; color: #198754; }
  .type-info { display: flex; flex-direction: column; gap: 4px; }
  .type-title { font-size: 16px; font-weight: 700; color: #1a202c; }
  .type-desc { font-size: 13px; color: #6b7280; }
  .theme-formation-card { background: linear-gradient(135deg,#f0fdf4,#dcfce7); border: 2px solid #198754; border-radius: 16px; padding: 24px; margin-bottom: 32px; }
  .theme-formation-card .card-header { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
  .theme-formation-card .card-icon { font-size: 28px; color: #198754; }
  .theme-formation-card h3 { font-size: 20px; font-weight: 700; color: #1a202c; }
  .theme-formation-card .card-body { display: flex; flex-direction: column; gap: 16px; }
  .progress-bar { height: 6px; background: #e5e7eb; border-radius: 10px; overflow: hidden; margin-bottom: 32px; }
  .progress-fill { height: 100%; background: linear-gradient(135deg,#198754,#20c997); transition: width 0.4s ease; }
  .progress-steps { display: flex; justify-content: space-between; margin-bottom: 40px; }
  .step { display: flex; flex-direction: column; align-items: center; gap: 8px; flex: 1; }
  .step-circle { width: 44px; height: 44px; border-radius: 50%; background: #e5e7eb; color: #9ca3af; display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 700; transition: all 0.3s ease; }
  .step.active .step-circle { background: linear-gradient(135deg,#198754,#20c997); color: white; box-shadow: 0 4px 12px rgba(25,135,84,0.3); }
  .step-label { font-size: 13px; font-weight: 600; color: #9ca3af; }
  .step.active .step-label { color: #1a202c; }
  .form-step { animation: fadeIn 0.5s ease; }
  @keyframes fadeIn { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
  .step-title { font-size: 28px; font-weight: 800; color: #1a202c; margin-bottom: 8px; }
  .step-description { font-size: 15px; color: #6b7280; margin-bottom: 32px; }
  .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
  .form-group { margin-bottom: 20px; }
  .form-group label { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: #374151; margin-bottom: 8px; }
  .form-group label svg { font-size: 14px; color: #198754; }
  .form-group input, .form-group select, .form-group textarea { width: 100%; padding: 12px 16px; border: 2px solid #e5e7eb; border-radius: 10px; font-size: 15px; font-family: inherit; transition: all 0.3s ease; background: white; }
  .form-group input:focus, .form-group select:focus, .form-group textarea:focus { outline: none; border-color: #198754; box-shadow: 0 0 0 4px rgba(25,135,84,0.1); }
  .form-group textarea { resize: vertical; min-height: 120px; }
  .char-count { text-align: right; font-size: 12px; color: #9ca3af; margin-top: 4px; }
  .help-text { font-size: 13px; color: #6b7280; margin-top: 6px; }
  .help-text a { color: #198754; text-decoration: none; font-weight: 600; }
  .radio-group { display: flex; gap: 16px; }
  .radio-label { display: flex; align-items: center; gap: 8px; padding: 10px 20px; background: #f9fafb; border: 2px solid #e5e7eb; border-radius: 10px; cursor: pointer; transition: all 0.3s ease; font-weight: 600; }
  .radio-label:has(input:checked) { background: #f0fdf4; border-color: #198754; color: #198754; }
  .payment-methods { display: grid; grid-template-columns: repeat(2,1fr); gap: 16px; }
  .payment-card { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 24px; background: #f9fafb; border: 2px solid #e5e7eb; border-radius: 12px; cursor: pointer; transition: all 0.3s ease; position: relative; }
  .payment-card input { position: absolute; opacity: 0; }
  .payment-card:hover { border-color: #198754; }
  .payment-card.selected { background: #f0fdf4; border-color: #198754; }
  .payment-icon { font-size: 32px; color: #198754; }
  .payment-card span { font-size: 15px; font-weight: 600; color: #374151; }
  .payment-info-box { display: flex; align-items: center; gap: 12px; padding: 16px; background: #eff6ff; border-radius: 10px; margin-top: 16px; font-size: 14px; color: #1e40af; }
  .conditions-section { margin-top: 24px; padding: 20px; background: #f9fafb; border-radius: 12px; }
  .checkbox-label { display: flex; align-items: flex-start; gap: 12px; font-size: 14px; color: #374151; cursor: pointer; margin-bottom: 12px; }
  .checkbox-label input { width: 18px; height: 18px; margin-top: 2px; }
  .checkbox-label a { color: #198754; text-decoration: none; font-weight: 600; }
  .security-badge { display: flex; align-items: center; gap: 16px; padding: 20px; background: linear-gradient(135deg,#f0fdf4,#dcfce7); border-radius: 12px; margin-top: 24px; }
  .security-icon { font-size: 32px; color: #198754; }
  .form-navigation { display: flex; gap: 16px; margin-top: 40px; padding-top: 24px; border-top: 2px solid #f3f4f6; }
  .nav-spacer { flex: 1; }
  .btn-prev, .btn-next, .btn-submit { display: flex; align-items: center; gap: 10px; padding: 14px 32px; border-radius: 12px; font-size: 16px; font-weight: 700; cursor: pointer; transition: all 0.3s ease; font-family: inherit; border: none; }
  .btn-prev { background: white; color: #374151; border: 2px solid #e5e7eb; }
  .btn-prev:hover { background: #f9fafb; }
  .btn-next, .btn-submit { background: linear-gradient(135deg,#198754,#20c997); color: white; box-shadow: 0 4px 12px rgba(25,135,84,0.3); }
  .btn-next:hover:not(:disabled), .btn-submit:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(25,135,84,0.4); }
  .btn-next:disabled, .btn-submit:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
  .sidebar-recap { position: relative; }
  .recap-card { background: white; border-radius: 20px; padding: 28px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
  .recap-card.sticky { position: sticky; top: 120px; }
  .recap-card h3 { font-size: 20px; font-weight: 800; color: #1a202c; margin-bottom: 20px; }
  .recap-session { display: flex; gap: 12px; margin-bottom: 20px; }
  .recap-session img { width: 80px; height: 80px; border-radius: 12px; object-fit: cover; }
  .recap-session-info h4 { font-size: 15px; font-weight: 700; color: #1a202c; margin-bottom: 8px; line-height: 1.3; }
  .recap-meta { display: flex; flex-direction: column; gap: 6px; }
  .recap-meta span { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #6b7280; }
  .recap-divider { height: 1px; background: #e5e7eb; margin: 20px 0; }
  .recap-line { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; font-size: 14px; }
  .recap-line span:first-child { color: #6b7280; display: flex; align-items: center; gap: 8px; }
  .recap-line.option span:first-child, .recap-line.special span:first-child { color: #198754; font-weight: 600; }
  .recap-line .price { font-weight: 700; color: #1a202c; }
  .recap-total { display: flex; justify-content: space-between; align-items: center; padding: 16px; background: #f9fafb; border-radius: 10px; font-size: 16px; font-weight: 700; }
  .total-price { font-size: 24px; color: #198754; }
  .recap-payment-info { display: flex; align-items: center; gap: 8px; margin-top: 12px; padding: 12px; background: #eff6ff; border-radius: 8px; font-size: 13px; color: #1e40af; font-weight: 600; }
  .recap-features { margin-top: 20px; padding-top: 20px; border-top: 1px solid #e5e7eb; }
  .feature-item { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; font-size: 14px; color: #374151; }
  .feature-item svg { color: #198754; font-size: 16px; }
  .help-box { display: flex; align-items: center; gap: 12px; margin-top: 20px; padding: 16px; background: linear-gradient(135deg,#dcfce7,#bbf7d0); border-radius: 12px; }
  .help-icon { font-size: 28px; color: #198754; }
  .help-box strong { font-size: 14px; color: #1a202c; display: block; margin-bottom: 2px; }
  .help-box p { font-size: 16px; font-weight: 700; color: #198754; }
  .success-page { min-height: 100vh; padding: 60px 24px; background: linear-gradient(135deg,#f0fdf4,#dcfce7,#d1fae5); position: relative; overflow: hidden; }
  .success-container { max-width: 900px; margin: 0 auto; position: relative; z-index: 1; }
  .success-animation { display: flex; justify-content: center; margin-bottom: 40px; }
  .success-checkmark { width: 120px; height: 120px; background: linear-gradient(135deg,#198754,#20c997); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 60px; box-shadow: 0 20px 60px rgba(25,135,84,0.3); animation: scaleIn 0.6s cubic-bezier(0.68,-0.55,0.265,1.55); }
  @keyframes scaleIn { 0%{transform:scale(0) rotate(-180deg);opacity:0} 100%{transform:scale(1) rotate(0deg);opacity:1} }
  .success-title { font-size: 42px; font-weight: 900; color: #1a202c; text-align: center; margin-bottom: 16px; }
  .success-subtitle { font-size: 18px; color: #6b7280; text-align: center; margin-bottom: 48px; }
  .confirmation-card-modern { background: white; border-radius: 24px; padding: 40px; box-shadow: 0 20px 60px rgba(0,0,0,0.1); margin-bottom: 40px; position: relative; }
  .confirmation-badge { position: absolute; top: -20px; right: 40px; background: linear-gradient(135deg,#198754,#20c997); color: white; padding: 8px 20px; border-radius: 20px; display: flex; align-items: center; gap: 8px; font-weight: 700; font-size: 14px; }
  .confirmation-header-modern { display: flex; align-items: center; gap: 20px; padding-bottom: 24px; border-bottom: 2px solid #f3f4f6; margin-bottom: 32px; }
  .confirmation-icon-wrapper { width: 70px; height: 70px; background: linear-gradient(135deg,#f0fdf4,#dcfce7); border-radius: 16px; display: flex; align-items: center; justify-content: center; }
  .confirmation-icon { font-size: 36px; color: #198754; }
  .confirmation-header-modern h3 { font-size: 16px; font-weight: 600; color: #6b7280; margin-bottom: 4px; }
  .confirmation-number { font-size: 28px; font-weight: 900; color: #1a202c; font-family: 'Courier New',monospace; letter-spacing: 1px; }
  .confirmation-details-modern { display: grid; grid-template-columns: repeat(2,1fr); gap: 16px; margin-bottom: 32px; }
  .detail-card { display: flex; align-items: flex-start; gap: 16px; padding: 20px; background: #f9fafb; border-radius: 16px; border: 2px solid transparent; transition: all 0.3s ease; }
  .detail-card:hover { background: #f0fdf4; border-color: #198754; }
  .detail-card.highlight { background: linear-gradient(135deg,#f0fdf4,#dcfce7); border-color: #198754; }
  .detail-icon { width: 48px; height: 48px; background: white; border-radius: 12px; display: flex; align-items: center; justify-content: center; color: #198754; font-size: 24px; flex-shrink: 0; }
  .detail-label { display: block; font-size: 13px; font-weight: 600; color: #9ca3af; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
  .detail-value { display: block; font-size: 16px; font-weight: 700; color: #1a202c; }
  .price-highlight { font-size: 24px; color: #198754; }
  .participant-info { background: #f9fafb; border-radius: 16px; padding: 24px; }
  .participant-info h4 { font-size: 18px; font-weight: 700; color: #1a202c; margin-bottom: 16px; }
  .participant-details { display: grid; grid-template-columns: repeat(2,1fr); gap: 12px; }
  .participant-item { display: flex; align-items: center; gap: 10px; font-size: 14px; color: #374151; padding: 10px; background: white; border-radius: 8px; }
  .participant-item svg { color: #198754; }
  .next-steps-modern { margin-bottom: 40px; }
  .next-steps-modern h3 { font-size: 24px; font-weight: 800; color: #1a202c; text-align: center; margin-bottom: 32px; }
  .steps-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 24px; }
  .step-card { background: white; border-radius: 20px; padding: 28px; text-align: center; box-shadow: 0 10px 30px rgba(0,0,0,0.08); position: relative; transition: all 0.3s ease; }
  .step-card:hover { transform: translateY(-8px); }
  .step-number-modern { position: absolute; top: -16px; left: 50%; transform: translateX(-50%); width: 40px; height: 40px; background: linear-gradient(135deg,#198754,#20c997); color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 900; }
  .step-icon-modern { width: 70px; height: 70px; background: linear-gradient(135deg,#f0fdf4,#dcfce7); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 20px auto; font-size: 32px; color: #198754; }
  .step-card h4 { font-size: 18px; font-weight: 700; color: #1a202c; margin-bottom: 12px; }
  .step-card p { font-size: 14px; color: #6b7280; line-height: 1.6; }
  .success-actions-modern { display: flex; justify-content: center; gap: 16px; flex-wrap: wrap; margin-bottom: 40px; }
  .btn-action { display: flex; align-items: center; gap: 10px; padding: 14px 28px; border-radius: 12px; font-size: 15px; font-weight: 700; cursor: pointer; transition: all 0.3s ease; font-family: inherit; border: none; }
  .btn-action.primary { background: linear-gradient(135deg,#198754,#20c997); color: white; box-shadow: 0 4px 12px rgba(25,135,84,0.3); }
  .btn-action.outline { background: white; color: #374151; border: 2px solid #e5e7eb; }
  .btn-action.outline:hover { border-color: #198754; color: #198754; }
  .contact-support-modern { background: white; border-radius: 20px; padding: 32px; box-shadow: 0 10px 30px rgba(0,0,0,0.08); display: flex; align-items: center; gap: 24px; margin-bottom: 24px; }
  .support-icon { width: 70px; height: 70px; background: linear-gradient(135deg,#25D366,#128C7E); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 36px; color: white; flex-shrink: 0; }
  .support-content h4 { font-size: 20px; font-weight: 700; color: #1a202c; margin-bottom: 4px; }
  .support-content p { font-size: 14px; color: #6b7280; margin-bottom: 12px; }
  .whatsapp-link { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; background: linear-gradient(135deg,#25D366,#128C7E); color: white; border-radius: 10px; text-decoration: none; font-weight: 600; font-size: 14px; }
  .catalogue-link { background: white; border-radius: 16px; padding: 24px; box-shadow: 0 10px 30px rgba(0,0,0,0.08); display: flex; align-items: center; gap: 16px; }
  .catalogue-link svg { font-size: 32px; color: #198754; }
  .catalogue-link a { color: #198754; text-decoration: none; font-weight: 700; margin-left: 4px; }
  @media (max-width: 1024px) { .inscription-layout { grid-template-columns: 1fr; } .sidebar-recap { order: -1; } .recap-card.sticky { position: static; } }
  @media (max-width: 768px) { .form-row { grid-template-columns: 1fr; } .type-formation-options { grid-template-columns: 1fr; } .payment-methods { grid-template-columns: 1fr; } .confirmation-details-modern { grid-template-columns: 1fr; } .steps-grid { grid-template-columns: 1fr; } .success-title { font-size: 32px; } }
`;